import { useState, useEffect } from "react";
import { workspaceHeaders } from "../lib/workspace-token";
import { sensitiveHeadersFor, fetchWithPasskey, rulesSha256, type PasskeyAction } from "../lib/passkey";
import { PasskeyChallenge } from "./PasskeyChallenge";
import type { PolicyRuleBody, PolicyVersion } from "../shared/types";
import { useFrozen } from "../context/FrozenContext";
import { apiUrl } from "../lib/apiBase";
import { useTranslation } from "react-i18next";
import { mapCommonError } from "../lib/error-message";
import { sanitizeRule, ValueRuleFields, ValueRuleSummary, FraudCheckSelect, AccountControlWarning } from "./policy/PolicyRuleFields";
import { ScheduledSendsList, SimplePolicyEditor, SimplePolicySummary } from "./policy/simple-policy-panel";
import { PolicyUpdateCard } from "./policy/policy-update-card";
import type { SimplePolicy } from "../lib/simple-policy-api";

type RuleType = PolicyRuleBody["type"];
type RuleEffect = PolicyRuleBody["effect"];

const EMPTY_RULE: PolicyRuleBody = {
  priority: 0,
  type: "transfer",
  effect: "allow",
  asset: null,
  amountMax: null,
  usdMax: null,
  toAddress: null,
  fraudCheck: null,
};

function formatCountdown(effectiveAt: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const diff = new Date(effectiveAt).getTime() - Date.now();
  if (diff <= 0) return t("policy.activatingSoon");
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return t("policy.remaining", { h, m });
}

export function PolicyRules({
  keyId,
  keyName,
  onClose,
}: {
  keyId: string;
  keyName?: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const frozen = useFrozen();
  const [active, setActive] = useState<PolicyVersion | null>(null);
  const [pending, setPending] = useState<PolicyVersion | null>(null);
  const [loading, setLoading] = useState(true);
  // A failed fetch is never shown as "No policy configured"
  const [loadFailed, setLoadFailed] = useState(false);
  const [defaultSimple, setDefaultSimple] = useState<SimplePolicy | null>(null);
  // Simple-settings editor open, starting from these settings.
  const [simpleEdit, setSimpleEdit] = useState<SimplePolicy | null>(null);
  // Simple mode hides the full rule list under "Custom (expert)".
  const [showCustom, setShowCustom] = useState(false);
  const [notice, setNotice] = useState("");

  // Draft editing state
  const [draft, setDraft] = useState<PolicyRuleBody[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [newRule, setNewRule] = useState<PolicyRuleBody>({ ...EMPTY_RULE });

  // Passkey challenge
  // The policy.set binding for the draft being submitted (its rules hash); null = no prompt.
  const [passkeyAction, setPasskeyAction] = useState<PasskeyAction | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Countdown refresh
  const [, setTick] = useState(0);

  async function fetchPolicy() {
    setLoading(true);
    setLoadFailed(false);
    try {
      const res = await fetch(apiUrl(`/api/keys/${keyId}/rules`), {
        headers: await workspaceHeaders(keyId),
      });
      if (!res.ok) throw new Error(`rules ${res.status}`);
      const data = await res.json();
      setActive(data.active);
      setPending(data.pending);
      setDefaultSimple(data.defaultSimple ?? null);
    } catch {
      setLoadFailed(true);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyId]);

  // Refresh countdown every 30s while pending exists
  useEffect(() => {
    if (!pending) return;
    const interval = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, [pending]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (draft) setDraft(null);
        else onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, draft]);

  function startEditing() {
    const rules = active?.rules ?? [];
    setDraft(rules.map((r, i) => ({ ...r, priority: i })));
    setAdding(false);
    setNewRule({ ...EMPTY_RULE });
  }

  function addRuleToDraft() {
    if (!draft) return;
    setDraft([...draft, { ...sanitizeRule(newRule), priority: draft.length }]);
    setNewRule({ ...EMPTY_RULE });
    setAdding(false);
  }

  function removeFromDraft(idx: number) {
    if (!draft) return;
    setDraft(draft.filter((_, i) => i !== idx).map((r, i) => ({ ...r, priority: i })));
  }

  function moveDraft(idx: number, direction: "up" | "down") {
    if (!draft) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= draft.length) return;
    const next = [...draft];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setDraft(next.map((r, i) => ({ ...r, priority: i })));
  }

  // After passkey auth, submit the draft
  async function submitDraft(ticket?: string) {
    if (!draft) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(apiUrl(`/api/keys/${keyId}/rules/pending`), {
        method: "POST",
        headers: await sensitiveHeadersFor(keyId, ticket),
        body: JSON.stringify({ rules: draft }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(mapCommonError(data.error ?? "", t) ?? t("policy.submitFailed"));
        setSubmitting(false);
        return;
      }
      setDraft(null);
      await fetchPolicy();
    } catch (err) {
      setError(mapCommonError(err, t) ?? t("policy.submitFailed"));
    }
    setSubmitting(false);
  }

  async function cancelPending() {
    setError("");
    try {
      const res = await fetchWithPasskey(apiUrl(`/api/keys/${keyId}/rules/pending`), {
        method: "DELETE",
        headers: await sensitiveHeadersFor(keyId),
      }, { action: "policy.cancel", keyShareId: keyId });
      if (!res.ok) {
        const data = await res.json();
        setError(mapCommonError(data.error ?? "", t) ?? t("policy.cancelFailed"));
        return;
      }
      await fetchPolicy();
    } catch (err) {
      setError(mapCommonError(err, t) ?? t("policy.cancelFailed"));
    }
  }

  const effectColor = (effect: string) =>
    effect === "allow"
      ? "text-green-400 bg-green-500/10"
      : "text-red-400 bg-red-500/10";

  function renderRuleRow(rule: PolicyRuleBody, idx: number, opts?: {
    editable?: boolean;
    total?: number;
  }) {
    return (
      <div
        key={idx}
        className="flex items-center gap-2 px-3 py-2.5 bg-surface-primary rounded-lg border border-border-secondary"
      >
        <span className="text-[10px] text-text-muted w-5 text-center shrink-0">
          {rule.priority}
        </span>

        <span
          className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${effectColor(rule.effect)} shrink-0`}
        >
          {rule.effect.toUpperCase()}
        </span>

        <div className="flex-1 min-w-0 text-xs text-text-secondary">
          {rule.type === "personal_sign" ? (
            <span>{t("policy.personalSign")}</span>
          ) : rule.type === "typed_message" ? (
            <span>{t("policy.typedData")}</span>
          ) : rule.type === "raw_message" ? (
            <span>{t("policy.rawMessage")}</span>
          ) : rule.type === "contract_call" ? (
            <span>
              {t("policy.contractCall")}
              {rule.toAddress ? (
                <span className="text-text-muted font-mono text-[10px]">
                  {" "}contract={rule.toAddress.slice(0, 10)}…
                </span>
              ) : (
                <span className="text-text-muted"> ({t("policy.anyContract")})</span>
              )}
              {rule.fraudCheck && (
                <span className={`text-[10px] ${rule.fraudCheck === "low" ? "text-green-400" : rule.fraudCheck === "medium" ? "text-yellow-400" : "text-red-400"}`}> {"\uD83D\uDEE1\uFE0F"} {rule.fraudCheck === "low" ? t("policy.fraudMax") : rule.fraudCheck === "medium" ? t("policy.fraudStrict") : t("policy.fraudStandard")}</span>
              )}
            </span>
          ) : rule.type === "value_message" ? (
            <span>
              {t("policy.ruleValueMessage")}
              {rule.toAddress && <span className="text-text-muted font-mono text-[10px]"> contract={rule.toAddress.slice(0, 10)}…</span>}
            </span>
          ) : rule.type === "account_control" ? (
            <span className="text-red-400">{t("policy.ruleAccountControl")}</span>
          ) : (
            <ValueRuleSummary rule={rule} t={t} />
          )}
        </div>

        {opts?.editable && (
          <>
            <div className="flex flex-col shrink-0">
              <button
                onClick={() => moveDraft(idx, "up")}
                disabled={idx === 0}
                className="text-text-muted hover:text-text-secondary disabled:opacity-20 p-0.5"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <button
                onClick={() => moveDraft(idx, "down")}
                disabled={idx === (opts.total ?? 0) - 1}
                className="text-text-muted hover:text-text-secondary disabled:opacity-20 p-0.5"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            <button
              onClick={() => removeFromDraft(idx)}
              className="text-text-muted hover:text-red-400 p-1 shrink-0 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </>
        )}
      </div>
    );
  }

  function renderAddForm() {
    return (
      <div className="bg-surface-primary rounded-lg border border-blue-500/30 p-3 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-text-muted block mb-1">{t("policy.type")}</label>
            <select
              value={newRule.type}
              onChange={(e) => setNewRule({ ...newRule, type: e.target.value as RuleType })}
              className="w-full bg-surface-secondary border border-border-primary rounded px-2 py-1.5 text-xs text-text-primary"
            >
              <option value="transfer">{t("policy.ruleTransfer")}</option>
              <option value="contract_call">{t("policy.ruleContractCall")}</option>
              <option value="approval">{t("policy.ruleApproval")}</option>
              <option value="account_control">{t("policy.ruleAccountControl")}</option>
              <option value="value_message">{t("policy.ruleValueMessage")}</option>
              <option value="personal_sign">{t("policy.rulePersonalSign")}</option>
              <option value="typed_message">{t("policy.ruleTypedMessage")}</option>
              <option value="raw_message">{t("policy.ruleRawMessage")}</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-text-muted block mb-1">{t("policy.effect")}</label>
            <select
              value={newRule.effect}
              onChange={(e) => setNewRule({ ...newRule, effect: e.target.value as RuleEffect })}
              className="w-full bg-surface-secondary border border-border-primary rounded px-2 py-1.5 text-xs text-text-primary"
            >
              <option value="allow">{t("policy.allow")}</option>
              <option value="block">{t("policy.block")}</option>
            </select>
          </div>
        </div>

        {(newRule.type === "transfer" || newRule.type === "approval") && (
          <ValueRuleFields rule={newRule} onChange={setNewRule} t={t} />
        )}

        {newRule.type === "account_control" && <AccountControlWarning t={t} />}

        {(newRule.type === "contract_call" || newRule.type === "value_message") && (
          <div className="space-y-2">
            {newRule.type === "value_message" && <p className="text-[10px] text-text-muted leading-relaxed">{t("policy.valueMessageHint")}</p>}
            <div>
              <label className="text-[10px] text-text-muted block mb-1">
                {t("policy.contractAddress")} <span className="text-text-muted/50">({t("policy.contractAddressHint")})</span>
              </label>
              <input
                value={newRule.toAddress || ""}
                onChange={(e) => setNewRule({ ...newRule, toAddress: e.target.value || null })}
                placeholder="0x..."
                className="w-full bg-surface-secondary border border-border-primary rounded px-2 py-1.5 text-xs text-text-primary font-mono"
              />
            </div>
            <FraudCheckSelect rule={newRule} onChange={setNewRule} hint={t("policy.fraudCheckContracts")} t={t} />
          </div>
        )}

        {newRule.amountMax && (!newRule.asset || newRule.asset === "*") && (
          <p className="text-[10px] text-red-400">{t("policy.assetRequired")}</p>
        )}
        <div className="flex gap-2">
          <button
            onClick={addRuleToDraft}
            disabled={!!(newRule.amountMax && (!newRule.asset || newRule.asset === "*"))}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs px-3 py-1.5 rounded transition-colors"
          >
            {t("policy.addRuleButton")}
          </button>
          <button
            onClick={() => setAdding(false)}
            className="text-text-tertiary hover:text-text-secondary text-xs px-3 py-1.5 rounded hover:bg-surface-tertiary transition-colors"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    );
  }

  // ── Passkey challenge overlay ──
  if (passkeyAction) {
    return (
      <PasskeyChallenge
        action={passkeyAction}
        onAuthenticated={(r) => {
          setPasskeyAction(null);
          submitDraft(r.token);
        }}
        onCancel={() => setPasskeyAction(null)}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-surface-secondary border border-border-primary rounded-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border-primary">
          <div>
            <h3 className="text-sm font-semibold text-text-primary">
              {draft ? t("policy.editPolicy") : t("policy.policyRules")}
            </h3>
            <p className="text-[11px] text-text-muted mt-0.5">
              {keyName || `Account ${keyId.slice(0, 8)}`}
              {draft ? ` — ${t("policy.editingDraft")}` : ` — ${t("policy.firstMatchWins")}`}
            </p>
          </div>
          <button
            onClick={() => { if (draft) setDraft(null); else onClose(); }}
            className="p-1.5 rounded-md text-text-tertiary hover:text-text-primary hover:bg-surface-tertiary transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {loading ? (
            <div className="text-xs text-text-muted text-center py-8">{t("common.loading")}</div>
          ) : loadFailed ? (
            <div className="text-center py-8 space-y-3" role="alert">
              <p className="text-xs text-red-400">{t("policy.loadFailed")}</p>
              <button
                onClick={fetchPolicy}
                className="text-xs text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded hover:bg-blue-500/10 transition-colors"
              >
                {t("common.retry")}
              </button>
            </div>
          ) : simpleEdit ? (
            <SimplePolicyEditor
              keyId={keyId}
              initial={simpleEdit}
              replacesCustom={active && !active.simple ? (active.rules as PolicyRuleBody[]).length : 0}
              onCancel={() => setSimpleEdit(null)}
              onDone={(applied) => {
                setSimpleEdit(null);
                setNotice(applied ? t("simplePolicy.appliedNow") : t("simplePolicy.scheduled24h"));
                fetchPolicy();
              }}
            />
          ) : draft ? (
            // ── Draft editing mode ──
            <>
              {draft.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-xs text-text-muted">{t("policy.noRulesSigningBlocked")}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {draft.map((rule, idx) =>
                    renderRuleRow(rule, idx, { editable: true, total: draft.length }),
                  )}
                </div>
              )}

              {adding ? renderAddForm() : (
                <button
                  onClick={() => setAdding(true)}
                  className="w-full text-xs text-blue-400 hover:text-blue-300 py-2 rounded-lg border border-dashed border-border-secondary hover:border-blue-500/30 transition-colors"
                >
                  + {t("policy.addRule")}
                </button>
              )}
            </>
          ) : (
            // ── View mode ──
            <>
              {/* Pending version banner */}
              {pending && pending.effectiveAt && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2.5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-yellow-400">{t("policy.pendingPolicyChange")}</p>
                      <p className="text-[10px] text-yellow-400/70 mt-0.5">
                        {t("policy.activatesIn", { time: formatCountdown(pending.effectiveAt, t) })}
                      </p>
                    </div>
                    {!frozen && (
                      <button
                        onClick={cancelPending}
                        className="text-[10px] text-yellow-400 hover:text-yellow-300 px-2 py-1 rounded bg-yellow-500/10 hover:bg-yellow-500/20 transition-colors"
                      >
                        {t("common.cancel")}
                      </button>
                    )}
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {pending.simple ? <SimplePolicySummary value={pending.simple} /> : (pending.rules as PolicyRuleBody[]).map((rule, idx) =>
                      renderRuleRow(rule, idx),
                    )}
                  </div>
                </div>
              )}

              {notice && <p className="text-xs rounded-lg px-3 py-2 bg-green-500/10 text-green-700 dark:text-green-400">{notice}</p>}
              {/* Simple policy (3 settings) */}
              {active?.simple && (
                <div className="space-y-3">
                  <p className="text-[10px] text-text-muted uppercase tracking-wide">{t("simplePolicy.title")}</p>
                  <SimplePolicySummary value={active.simple} />
                  {!frozen && (
                    <button onClick={() => setSimpleEdit(active.simple!)} className="w-full text-sm font-medium text-blue-600 dark:text-blue-400 py-2 rounded-lg border border-border-secondary hover:bg-blue-500/5">
                      {t("simplePolicy.change")}
                    </button>
                  )}
                  <ScheduledSendsList keyId={keyId} />
                  <button onClick={() => setShowCustom(!showCustom)} className="text-xs text-text-tertiary hover:text-text-primary">
                    {showCustom ? "▾" : "▸"} {t("simplePolicy.customExpert")}
                  </button>
                </div>
              )}
              {active && !active.simple && (
                <>
                  <span className="inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-surface-tertiary text-text-secondary">{t("simplePolicy.customBadge")}</span>
                  {!frozen && defaultSimple && (
                    <PolicyUpdateCard keyId={keyId} onUpdate={() => setSimpleEdit(defaultSimple)} />
                  )}
                </>
              )}
              {/* Active rules */}
              {active && active.simple && !showCustom ? null : active ? (
                <div className="space-y-2">
                  <p className="text-[10px] text-text-muted uppercase tracking-wide">{t("policy.activeRules")}</p>
                  <p className="text-[10px] text-text-muted leading-relaxed">{t("policy.semanticsNotice")}</p>
                  {(active.rules as PolicyRuleBody[]).length === 0 ? (
                    <p className="text-xs text-text-muted text-center py-4">{t("policy.noRulesSigningBlockedView")}</p>
                  ) : (
                    (active.rules as PolicyRuleBody[]).map((rule, idx) =>
                      renderRuleRow(rule, idx),
                    )
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-xs text-text-muted">{t("policy.noPolicyConfigured")}</p>
                  <p className="text-[10px] text-text-muted/60 mt-1">
                    {t("policy.withoutRulesSigningBlocked")}
                  </p>
                </div>
              )}
            </>
          )}

          {error && (
            <p className="text-[10px] text-red-400 text-center">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border-primary flex items-center justify-between">
          <p className="text-[10px] text-text-muted leading-relaxed max-w-[60%]">
            {draft
              ? t("policy.requiresPasskeyFooter")
              : t("policy.firstMatchNoMatch")}
          </p>

          {draft ? (
            <div className="flex gap-2">
              <button
                onClick={() => setDraft(null)}
                className="text-text-tertiary hover:text-text-secondary text-xs px-3 py-1.5 rounded hover:bg-surface-tertiary transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={async () => draft && setPasskeyAction({ action: "policy.set", keyShareId: keyId, rulesSha256: await rulesSha256(draft) })}
                disabled={submitting || frozen}
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs px-4 py-1.5 rounded transition-colors"
              >
                {submitting ? t("policy.submitting") : t("policy.submit")}
              </button>
            </div>
          ) : !pending && !frozen && !loadFailed && !simpleEdit && (!active?.simple || showCustom) ? (
            <button
              onClick={startEditing}
              className="text-blue-400 hover:text-blue-300 text-xs px-3 py-1.5 rounded hover:bg-blue-500/10 transition-colors"
            >
              {t("policy.editPolicyButton")}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
